import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { addItemAsync, fetchItemsAsync } from './inventorySlice';
import { getCategories } from '../../services/itemService';
import { getSuppliers, linkItemToSupplier, Supplier } from '../../services/supplierService';
import { restockItem } from '../../services/stockService';
import BarcodeScanner from '../../components/scanner/BarcodeScanner';
import ProductCamera from '../../components/camera/ProductCamera';
import { uploadItemImage } from '../../services/itemService';
import AddSupplierModal from '../suppliers/AddSupplierModal';

interface AddItemModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AddItemModal: React.FC<AddItemModalProps> = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const [categories, setCategories] = useState<string[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scanMode, setScanMode] = useState(true);
  const [showScanner, setShowScanner] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [capturedImage, setCapturedImage] = useState<string>('');
  const [newItemId, setNewItemId] = useState<number | null>(null);
  const [showAddSupplierModal, setShowAddSupplierModal] = useState(false);
  const barcodeInputRef = useRef<HTMLInputElement>(null);
  const scanBufferRef = useRef<string>('');
  const scanTimerRef = useRef<number | null>(null);

  const [formData, setFormData] = useState({
    sku: '',
    name: '',
    category: '',
    unit: 'pcs',
    cost_price: 0,
    sell_price: 0,
    min_stock: 5,
    description: '',
    barcode: '',
    has_warranty: true,
    // Supplier info
    supplier_id: 0,
    brand: '',
    supplier_sku: '',
    supplier_barcode: '',
    // Initial stock
    initial_stock: 0,
  });

  useEffect(() => {
    if (isOpen) {
      loadCategories();
      loadSuppliers();
      setScanMode(true);
      // Auto-focus barcode field for scanning
      setTimeout(() => {
        barcodeInputRef.current?.focus();
      }, 100);
    } else {
      // Reset scan mode when closed
      setScanMode(true);
      scanBufferRef.current = '';
    }
  }, [isOpen]);

  const loadCategories = async () => {
    try {
      const cats = await getCategories();
      setCategories(cats);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  const loadSuppliers = async () => {
    try {
      const data = await getSuppliers();
      setSuppliers(data);
    } catch (err) {
      console.error('Failed to load suppliers:', err);
    }
  };

  const handleSupplierAdded = (newSupplier: Supplier) => {
    setSuppliers(prev => [...prev, newSupplier]);
    setFormData(prev => ({ ...prev, supplier_id: newSupplier.id }));
    setShowAddSupplierModal(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Create item first
      const itemData = {
        sku: formData.sku,
        name: formData.name,
        category: formData.category,
        unit: formData.unit,
        cost_price: formData.cost_price,
        sell_price: formData.sell_price,
        min_stock: formData.min_stock,
        description: formData.description,
        barcode: formData.barcode,
        has_warranty: formData.has_warranty,
      };
      
      const newItem = await dispatch(addItemAsync(itemData) as any).unwrap();
      
      // If initial stock provided, add stock
      if (formData.initial_stock > 0 && currentUser?.branch_id) {
        try {
          await restockItem({
            item_id: newItem.id,
            branch_id: currentUser.branch_id,
            qty: formData.initial_stock,
            notes: 'Stok awal saat pembuatan item',
          });
        } catch (err) {
          console.error('Failed to add initial stock:', err);
          // Don't fail the whole operation if initial stock fails
        }
      }
      
      // If supplier selected, link item to supplier
      if (formData.supplier_id > 0) {
        try {
          await linkItemToSupplier({
            item_id: newItem.id,
            supplier_id: formData.supplier_id,
            brand: formData.brand || undefined,
            cost_price: formData.cost_price,
            supplier_sku: formData.supplier_sku || undefined,
            supplier_barcode: formData.supplier_barcode || undefined,
            lead_time_days: 0,
            is_preferred: true,
          });
        } catch (err) {
          console.error('Failed to link supplier:', err);
          // Don't fail the whole operation if linking fails
        }
      }
      
      // Reload items list after successful creation
      dispatch(fetchItemsAsync() as any);
      
      // Save item ID for image upload
      setNewItemId(newItem.id);
      
      // If there's a captured image, upload it
      if (capturedImage) {
        try {
          // Convert base64 to blob
          const response = await fetch(capturedImage);
          const blob = await response.blob();
          const file = new File([blob], 'product-photo.jpg', { type: 'image/jpeg' });
          
          await uploadItemImage(newItem.id, file);
          alert('Item dan foto berhasil ditambahkan!');
        } catch (imgErr) {
          console.error('Failed to upload image:', imgErr);
          alert('Item berhasil ditambahkan, tetapi gagal upload foto.');
        }
      }
      
      // Reset form
      setFormData({
        sku: '',
        name: '',
        category: '',
        unit: 'pcs',
        cost_price: 0,
        sell_price: 0,
        min_stock: 5,
        description: '',
        barcode: '',
        has_warranty: true,
        supplier_id: 0,
        brand: '',
        supplier_sku: '',
        supplier_barcode: '',
        initial_stock: 0,
      });
      setCapturedImage('');
      setNewItemId(null);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create item');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    let processedValue: any = value;
    
    // Handle different input types
    if (type === 'checkbox') {
      processedValue = (e.target as HTMLInputElement).checked;
    } else if (type === 'number') {
      processedValue = parseFloat(value) || 0;
    } else if (name === 'supplier_id') {
      // Ensure supplier_id is always a number
      processedValue = parseInt(value, 10) || 0;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
    
    // Debug log for supplier selection
    if (name === 'supplier_id') {
      console.log('Supplier selected:', processedValue, typeof processedValue);
    }
  };

  // Handle barcode scanner input
  const handleBarcodeKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Enter key confirms barcode scan
    if (e.key === 'Enter' && formData.barcode) {
      e.preventDefault();
      setScanMode(false);
      // Move to SKU field after scan
      setTimeout(() => {
        document.getElementsByName('sku')[0]?.focus();
      }, 100);
    }
  };

  const handleBarcodeFocus = () => {
    setScanMode(true);
  };
  
  const handleCameraCapture = (dataUri: string) => {
    setCapturedImage(dataUri);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[95vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
          <h2 className="text-xl font-bold text-gray-900">Add New Item</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
            type="button"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Barcode Scanner Section - Priority */}
          <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-semibold text-blue-900">
                📷 Scan Barcode (Optional)
              </label>
              {scanMode && (
                <span className="flex items-center gap-2 text-xs text-blue-600 animate-pulse">
                  <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                  Ready to scan...
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <input
                ref={barcodeInputRef}
                type="text"
                name="barcode"
                value={formData.barcode}
                onChange={handleChange}
                onKeyDown={handleBarcodeKeyDown}
                onFocus={handleBarcodeFocus}
                className="flex-1 px-4 py-3 border-2 border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg font-mono"
                placeholder="Scan or type barcode here..."
                autoComplete="off"
              />
              <button
                type="button"
                onClick={() => setShowScanner(true)}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 font-medium"
                title="Open Camera Scanner"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Scan Camera
              </button>
            </div>
            <p className="mt-2 text-xs text-gray-600">
              💡 Tip: Gunakan scanner hardware atau klik tombol "Scan Camera" untuk scan dengan kamera
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* SKU */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                SKU <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="sku"
                value={formData.sku}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., LCD-OPPO-A3S"
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="category"
                value={formData.category}
                onChange={handleChange}
                list="categories"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., LCD, Battery"
              />
              <datalist id="categories">
                {categories.map(cat => (
                  <option key={cat} value={cat} />
                ))}
              </datalist>
            </div>

            {/* Name */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Item Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., LCD Oppo A3s Fullset"
              />
            </div>

            {/* Unit */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Unit
              </label>
              <select
                name="unit"
                value={formData.unit}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="pcs">pcs</option>
                <option value="set">set</option>
                <option value="box">box</option>
                <option value="roll">roll</option>
              </select>
            </div>

            {/* Barcode Display (already scanned above) */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Barcode Scanned
              </label>
              <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 font-mono text-gray-900 font-semibold">
                {formData.barcode || '-'}
              </div>
            </div>

            {/* Supplier */}
            <div className="col-span-2">
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Supplier
                </label>
                <button
                  type="button"
                  onClick={() => setShowAddSupplierModal(true)}
                  className="text-xs px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 transition flex items-center gap-1"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Tambah
                </button>
              </div>
              <select
                name="supplier_id"
                value={formData.supplier_id}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="0">- No Supplier -</option>
                {suppliers.map(supplier => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.code} - {supplier.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Brand - Always visible */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Brand
              </label>
              <input
                type="text"
                name="brand"
                value={formData.brand}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Meetoo, Ximi, Vivan, Original"
              />
            </div>
            
            {/* Product Photo */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Foto Produk (Optional)
              </label>
              <div className="flex items-center gap-3">
                {capturedImage ? (
                  <div className="flex items-center gap-3 flex-1">
                    <img 
                      src={capturedImage} 
                      alt="Product preview" 
                      className="w-24 h-24 object-cover rounded-lg border-2 border-green-300"
                    />
                    <div className="flex-1">
                      <p className="text-sm text-green-600 font-medium">✓ Foto siap diupload</p>
                      <button
                        type="button"
                        onClick={() => setCapturedImage('')}
                        className="text-sm text-red-600 hover:underline mt-1 font-medium"
                      >
                        Hapus foto
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowCamera(true)}
                    className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2 font-medium"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Ambil Foto Produk
                  </button>
                )}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                💡 Foto akan otomatis diupload setelah item dibuat
              </p>
            </div>

            {/* Supplier SKU & Barcode - Only show if supplier selected */}
            {formData.supplier_id !== 0 && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Supplier SKU
                  </label>
                  <input
                    type="text"
                    name="supplier_sku"
                    value={formData.supplier_sku}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Supplier's product code"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Supplier Barcode
                  </label>
                  <input
                    type="text"
                    name="supplier_barcode"
                    value={formData.supplier_barcode}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Supplier's barcode (if different)"
                  />
                </div>
              </>
            )}

            {/* Cost Price */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cost Price (Rp)
              </label>
              <input
                type="number"
                name="cost_price"
                value={formData.cost_price}
                onChange={handleChange}
                min="0"
                step="1000"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Sell Price */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sell Price (Rp) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="sell_price"
                value={formData.sell_price}
                onChange={handleChange}
                min="0"
                step="1000"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Min Stock */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Minimum Stock
              </label>
              <input
                type="number"
                name="min_stock"
                value={formData.min_stock}
                onChange={handleChange}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Initial Stock */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Stok Awal
              </label>
              <input
                type="number"
                name="initial_stock"
                value={formData.initial_stock}
                onChange={handleChange}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Qty stok awal (opsional)"
              />
              <p className="mt-1 text-xs text-gray-500">
                💡 Akan otomatis ditambahkan ke cabang Anda
              </p>
            </div>

            {/* Has Warranty */}
            <div className="flex items-center pt-6">
              <input
                type="checkbox"
                name="has_warranty"
                checked={formData.has_warranty}
                onChange={handleChange}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label className="ml-2 text-sm font-medium text-gray-700">
                Has Warranty
              </label>
            </div>

            {/* Description */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Optional description"
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create Item'}
            </button>
          </div>
        </form>
      </div>
      
      {/* Barcode Scanner Modal */}
      <BarcodeScanner
        isOpen={showScanner}
        onScan={(code) => {
          setFormData(prev => ({ ...prev, barcode: code }));
          setShowScanner(false);
          setScanMode(false);
        }}
        onClose={() => setShowScanner(false)}
      />
      
      {/* Product Camera Modal */}
      <ProductCamera
        isOpen={showCamera}
        onCapture={handleCameraCapture}
        onClose={() => setShowCamera(false)}
      />

      {/* Add Supplier Modal */}
      <AddSupplierModal
        isOpen={showAddSupplierModal}
        onClose={() => setShowAddSupplierModal(false)}
        onSupplierAdded={handleSupplierAdded}
      />
    </div>
  );
};

export default AddItemModal;
